import React, { useState, useRef, useCallback } from 'react';
import {
  fetchIngestionCandidates,
  postIngestPriceData,
  postDebugConsensus,
} from '../utils/api';

const LOG_CAP = 200;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function appendLogLines(prev, newLines) {
  const next = [...prev, ...newLines];
  return next.length > LOG_CAP ? next.slice(-LOG_CAP) : next;
}

function DebugBulkIngest() {
  const [jobType, setJobType] = useState(null);
  const [total, setTotal] = useState(0);
  const [done, setDone] = useState(0);
  const [logLines, setLogLines] = useState([]);
  const [fatalError, setFatalError] = useState(null);
  const abortRef = useRef(null);

  const running = jobType !== null;

  const pushLog = useCallback((line) => {
    setLogLines((prev) => appendLogLines(prev, [line]));
  }, []);

  const stopJob = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const runPriceBulk = async () => {
    setFatalError(null);
    setLogLines([]);
    setJobType('price');
    setDone(0);
    setTotal(0);
    const ac = new AbortController();
    abortRef.current = ac;

    try {
      const { items } = await fetchIngestionCandidates({
        limit: 1000,
        signal: ac.signal,
      });
      if (!items?.length) {
        setFatalError('No companies returned from candidates endpoint.');
        setJobType(null);
        abortRef.current = null;
        return;
      }
      setTotal(items.length);
      pushLog(`Starting price fetch for ${items.length} companies (may take many minutes).`);

      for (let i = 0; i < items.length; i++) {
        if (ac.signal.aborted) {
          pushLog('Cancelled.');
          break;
        }
        const { ticker } = items[i];
        try {
          const res = await postIngestPriceData(ticker, { signal: ac.signal });
          pushLog(
            `${i + 1}/${items.length} ${ticker}: ${res.status || '?'} stored=${res.data_stored ?? 0} fetched=${res.data_fetched ?? 0}`
          );
        } catch (e) {
          if (e.name === 'AbortError') {
            pushLog('Cancelled.');
            break;
          }
          pushLog(`${i + 1}/${items.length} ${ticker}: error — ${e.message || e}`);
        }
        setDone(i + 1);
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        setFatalError(e.message || String(e));
      }
    } finally {
      abortRef.current = null;
      setJobType(null);
    }
  };

  const runConsensusBulk = async () => {
    setFatalError(null);
    setLogLines([]);
    setJobType('consensus');
    setDone(0);
    setTotal(0);
    const ac = new AbortController();
    abortRef.current = ac;

    try {
      const { items } = await fetchIngestionCandidates({
        limit: 1000,
        signal: ac.signal,
      });
      if (!items?.length) {
        setFatalError('No companies returned from candidates endpoint.');
        setJobType(null);
        abortRef.current = null;
        return;
      }
      setTotal(items.length);
      pushLog(
        `Starting MarketBeat consensus fetch for ${items.length} companies (slow; long pauses between requests).`
      );

      for (let i = 0; i < items.length; i++) {
        if (ac.signal.aborted) {
          pushLog('Cancelled.');
          break;
        }
        const { ticker } = items[i];
        try {
          const res = await postDebugConsensus(ticker, { signal: ac.signal });
          const errTail =
            res.errors?.length > 0 ? ` errors=${JSON.stringify(res.errors)}` : '';
          pushLog(
            `${i + 1}/${items.length} ${ticker}: ${res.status} rating_rows=${res.rating_records_created} target_rows=${res.price_target_records_created}${errTail}`
          );
        } catch (e) {
          if (e.name === 'AbortError') {
            pushLog('Cancelled.');
            break;
          }
          pushLog(`${i + 1}/${items.length} ${ticker}: error — ${e.message || e}`);
        }
        setDone(i + 1);
        if (i < items.length - 1 && !ac.signal.aborted) {
          await sleep(400);
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        setFatalError(e.message || String(e));
      }
    } finally {
      abortRef.current = null;
      setJobType(null);
    }
  };

  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="component debug-bulk-ingest">
      <div className="stocks-header">
        <h2>Bulk ingestion</h2>
      </div>
      <p className="no-price" style={{ marginTop: 0, marginBottom: '1rem' }}>
        Fetches run one ticker per request so the UI can show progress. Large universes can take a long time; you can cancel between tickers.
      </p>

      <div className="debug-bulk-actions">
        <button
          type="button"
          onClick={runPriceBulk}
          disabled={running}
        >
          Fetch price data (all companies)
        </button>
        <button
          type="button"
          onClick={runConsensusBulk}
          disabled={running}
        >
          Fetch consensus data (all companies)
        </button>
        <button
          type="button"
          className="debug-bulk-cancel"
          onClick={stopJob}
          disabled={!running}
        >
          Cancel
        </button>
      </div>

      {fatalError ? <div className="error">{fatalError}</div> : null}

      {total > 0 ? (
        <div className="debug-bulk-progress-wrap">
          <div className="debug-bulk-progress-meta">
            Progress: {done} / {total} processed ({total - done} remaining){' '}
            {running ? `— ${jobType === 'price' ? 'prices' : 'consensus'}` : ''}
          </div>
          <div className="debug-bulk-progress-bar">
            <div
              className="debug-bulk-progress-fill"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      ) : null}

      {logLines.length > 0 ? (
        <div className="debug-bulk-log" aria-live="polite">
          {logLines.map((line, idx) => (
            <p className="debug-bulk-log-line" key={idx}>
              {line}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default DebugBulkIngest;
